import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { FormikProvider, useFormik } from "formik"
import * as Yup from "yup"
import { makeStyles } from "tss-react/mui"
import { Box, Dialog, Divider } from "@mui/material"

import { USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE } from "../../../../../../constants/routes"
import {
    MAX_EMAIL_LENGTH,
    MAX_NAME_LENGTH,
    MAX_ORDER_COMMENT_LENGTH,
} from "../../../../../../constants/inputValidationDefaults"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { getIncludedVAT, toDineroOrUndefined, toMonetaryAmount } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { isPaymentGatewayDetails } from "../../../../../../modules/payments/paymentTypes"
import {
    isOrderResponse,
    isOrdersResponse,
    isPaymentPollingResponse,
} from "../../../../../../modules/payments/transactionTypes"
import { OrganizerType } from "../../../../../types/OrganizerTypes"
import { BuyerParty } from "../../../../../../modules/payments/orderTypes"
import { DeliveryFulfillmentType } from "../../form/formTypes"
import { StandardLoadingButton } from "../../../../buttons/LoadingButton"
import { SafeArea } from "../../../../skeleton/SafeArea"

import type { TakeawayCartItem, TakeawayKitchen, TakeawayOrderDelivery } from "../takeawayTypes"
import { createTakeawayOrder, fetchTakeawayPaymentDetails, TakeawayQuery } from "../takeawayApi"
import { useTakeawayCart } from "../TakeawayCartContext"

import { calcTotal, calcWalletTotal, CheckoutFormValues, isValidPaymentMethod } from "./takeawayCheckoutLib"
import { TakeawayCheckoutHeader } from "./TakeawayCheckoutHeader"
import { TakeawayCheckoutPaymentSection } from "./TakeawayCheckoutPaymentSection"
import { TakeawayCheckoutGuestSection } from "./TakeawayCheckoutGuestSection"
import { TakeawayCheckoutCommentSection } from "./TakeawayCheckoutCommentSection"
import { TakeawayCheckoutOrderSummary } from "./TakeawayCheckoutOrderSummary"
import { TakeawayCheckoutTermsSection } from "./TakeawayCheckoutTermsSection"

const useStyles = makeStyles()((theme) => ({
    layout: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        background: theme.palette.common.white,
    },
    body: {
        flex: 1,
        display: "flex",
        alignItems: "flex-start",
        maxWidth: "1440px",
        width: "100%",
        margin: "auto",
    },
    leftColumn: {
        width: "50%",
        flexShrink: 0,
        padding: "2rem 5rem",
        display: "flex",
        flexDirection: "column",
    },
    leftInner: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    rightColumn: {
        position: "sticky",
        top: "5.625rem",
        alignSelf: "flex-start",
        width: "50%",
        flexShrink: 0,
        padding: "2rem 6.4375rem",
    },
    confirmButton: {
        width: "100%",
        "&.Mui-disabled": {
            backgroundColor: theme.palette.grey[300],
        },
    },
}))

const initialValues: CheckoutFormValues = {
    selectedMethodValue: "",
    orderComment: "",
    accountingValues: {},
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    termsAccepted: false,
}

type TakeawayCheckoutDialogProps = Readonly<{
    cartByDate: Record<string, TakeawayCartItem[]>
    kitchen: TakeawayKitchen
    onClose: () => void
    onSuccess: () => void
    onCorrectOrder: () => void
}>

export function TakeawayCheckoutDialog({
    cartByDate,
    kitchen,
    onClose,
    onSuccess,
    onCorrectOrder,
}: TakeawayCheckoutDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const cart = useTakeawayCart()
    const navigate = useNavigate()

    const sortedDates = useMemo(() => Object.keys(cartByDate).sort(), [cartByDate])
    const allItems = useMemo(() => Object.values(cartByDate).flat(), [cartByDate])
    const totalPrice = useMemo(() => calcTotal(allItems), [allItems])
    const vatAmount = useMemo(() => getIncludedVAT(totalPrice), [totalPrice])
    const walletTotal = useMemo(() => calcWalletTotal(allItems), [allItems])

    const deliveryLocationName = cart?.deliveryKitchenName ?? kitchen.name

    const deliveries: TakeawayOrderDelivery[] = useMemo(
        () =>
            sortedDates.map((dateKey) => ({
                deliveryType: DeliveryFulfillmentType.PICK_UP,
                deliveryLocation: { name: deliveryLocationName },
                deliveryTime: dateTimeFactory.fromISO(dateKey).toISO() ?? dateKey,
                orderLines: (cartByDate[dateKey] ?? [])
                    .filter((item) => item.quantity > 0)
                    .map((item) => ({
                        productId: item.product.id,
                        items: item.quantity,
                        buyerParty: BuyerParty.PRIVATE,
                    })),
            })),
        [cartByDate, deliveryLocationName, sortedDates],
    )

    const { data: paymentDetails } = useApiQuery(fetchTakeawayPaymentDetails, {
        queryName: TakeawayQuery.TAKEAWAY_PAYMENT_DETAILS,
        dependencies: { kitchenId: kitchen.id, deliveries },
        enabled: allItems.length > 0,
    })

    const walletBalance = useMemo(
        () => toDineroOrUndefined(paymentDetails?.userAccountBalance),
        [paymentDetails?.userAccountBalance],
    )

    const fillGuestDetails = paymentDetails?.deliveryDetails?.fillGuestDetails ?? false
    const salesConditionsUrl = paymentDetails?.salesConditionsUrl

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                selectedMethodValue: Yup.string().required().label(l10n.getString("required-field")),
                guestName: fillGuestDetails
                    ? Yup.string().required().max(MAX_NAME_LENGTH).label(l10n.getString("required-field"))
                    : Yup.string(),
                guestEmail: fillGuestDetails
                    ? Yup.string().required().email().max(MAX_EMAIL_LENGTH).label(l10n.getString("required-field"))
                    : Yup.string(),
                orderComment: Yup.string()
                    .max(MAX_ORDER_COMMENT_LENGTH)
                    .label(l10n.getString("takeaway-confirm-order-comment-placeholder")),
                termsAccepted: salesConditionsUrl
                    ? Yup.boolean().oneOf([true], l10n.getString("takeaway-checkout-terms-not-accepted"))
                    : Yup.boolean(),
            }),
        [fillGuestDetails, salesConditionsUrl, l10n],
    )

    const { mutate: submitOrder, isLoading: isSubmitting } = useApiMutation(createTakeawayOrder, {
        async onSuccess(_, response) {
            // Payment gateway redirect takes priority -> CREDIT_CARD/MobilePay responses include
            // both `orders` and `paymentDetails`, but we must redirect to the payment window first.
            if (isPaymentPollingResponse(response)) {
                if (isPaymentGatewayDetails(response.paymentDetails)) {
                    window.location.href = response.paymentDetails.paymentWindowUrl
                    return
                }
                return
            }
            if (isOrdersResponse(response)) {
                navigate(USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE, {
                    state: { orders: response.orders, kitchen, totalPrice: toMonetaryAmount(totalPrice) },
                })
                onSuccess()
                return
            }
            if (isOrderResponse(response)) {
                navigate(USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE, {
                    state: { orders: [response.order], kitchen, totalPrice: toMonetaryAmount(totalPrice) },
                })
                onSuccess()
                return
            }
            // Fallback: navigate without order data
            navigate(USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE)
            onSuccess()
        },
    })

    const formik = useFormik<CheckoutFormValues>({
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            if (!values.selectedMethodValue) return

            const selectedMethod =
                paymentDetails?.paymentMethods.find((m) => m.value === values.selectedMethodValue) ?? null

            const dimensions = selectedMethod?.accounting?.dimensions
            const accountingDimensions = dimensions
                ? dimensions
                      .map((dim) => ({ id: dim.id, value: values.accountingValues[dim.inputField.name] ?? "" }))
                      .filter((dim) => dim.value !== undefined && dim.value !== null && dim.value !== "")
                : undefined

            const organizers = fillGuestDetails
                ? [
                      {
                          name: values.guestName,
                          email: values.guestEmail,
                          mobilePhone: values.guestPhone,
                          organizerType: OrganizerType.CREATOR,
                      },
                  ]
                : undefined

            submitOrder({
                request: {
                    kitchen: { id: kitchen.id },
                    webshop: { uid: kitchen.webshop?.uid ?? "" },
                    payment: {
                        method: values.selectedMethodValue,
                        ...(selectedMethod?.card ? { card: { id: selectedMethod.card.id } } : {}),
                        ...(accountingDimensions && accountingDimensions.length > 0
                            ? { accounting: { dimensions: accountingDimensions } }
                            : {}),
                    },
                    orderNote: values.orderComment,
                    deliveries,
                    ...(organizers ? { organizers } : {}),
                },
            })
        },
    })

    const selectedMethod = useMemo(
        () => paymentDetails?.paymentMethods.find((m) => m.value === formik.values.selectedMethodValue) ?? null,
        [paymentDetails?.paymentMethods, formik.values.selectedMethodValue],
    )

    const showSalesConditions = Boolean(salesConditionsUrl)

    useEffect(() => {
        const methods = paymentDetails?.paymentMethods ?? []
        if (!methods.length || formik.values.selectedMethodValue) return
        if (methods.length === 1 && isValidPaymentMethod(methods[0], walletTotal, walletBalance)) {
            formik.setFieldValue("selectedMethodValue", methods[0].value, false)
        }
    }, [paymentDetails])

    useEffect(() => {
        if (selectedMethod && !isValidPaymentMethod(selectedMethod, walletTotal, walletBalance)) {
            formik.setFieldValue("selectedMethodValue", "", false)
        }
    }, [selectedMethod, walletTotal, walletBalance])

    const canConfirm =
        !isSubmitting &&
        formik.values.selectedMethodValue !== "" &&
        (!fillGuestDetails || (formik.values.guestName !== "" && formik.values.guestEmail !== "")) &&
        (!showSalesConditions || formik.values.termsAccepted)

    return (
        <FormikProvider value={formik}>
            <Dialog open onClose={onClose} fullWidth fullScreen scroll="paper">
                <SafeArea>
                    <Box className={classes.layout}>
                        <TakeawayCheckoutHeader onGoBack={onCorrectOrder} onClose={onClose} />

                        <Box className={classes.body}>
                            <Box className={classes.leftColumn}>
                                <Box className={classes.leftInner}>
                                    {fillGuestDetails && (
                                        <>
                                            <TakeawayCheckoutGuestSection />
                                            <Divider />
                                        </>
                                    )}

                                    <TakeawayCheckoutCommentSection />

                                    <Divider />

                                    <TakeawayCheckoutPaymentSection
                                        methods={paymentDetails?.paymentMethods ?? []}
                                        selectedMethodValue={formik.values.selectedMethodValue}
                                        walletTotal={walletTotal}
                                        walletBalance={walletBalance}
                                        moneyFactory={moneyFactory}
                                        accountingValues={formik.values.accountingValues}
                                        notice={selectedMethod?.notice}
                                        accountingDimensions={selectedMethod?.accounting}
                                        onMethodSelect={(value) => {
                                            formik.setFieldValue("selectedMethodValue", value)
                                            formik.setFieldValue("accountingValues", {})
                                        }}
                                        onAccountingChange={(name, value) =>
                                            formik.setFieldValue("accountingValues", {
                                                ...formik.values.accountingValues,
                                                [name]: value,
                                            })
                                        }
                                    />

                                    {showSalesConditions && salesConditionsUrl && (
                                        <TakeawayCheckoutTermsSection salesConditionsUrl={salesConditionsUrl} />
                                    )}

                                    <StandardLoadingButton
                                        loading={isSubmitting}
                                        disabled={!canConfirm}
                                        onClick={formik.submitForm}
                                        color="black"
                                        size="large"
                                        className={classes.confirmButton}
                                    >
                                        <LocalizedStrict
                                            id="takeaway-checkout-confirm-button-with-price"
                                            vars={{ price: moneyFactory.format(totalPrice) }}
                                        >
                                            <span>Complete payment: {moneyFactory.format(totalPrice)}</span>
                                        </LocalizedStrict>
                                    </StandardLoadingButton>
                                </Box>
                            </Box>

                            <Box className={classes.rightColumn}>
                                <TakeawayCheckoutOrderSummary
                                    cartByDate={cartByDate}
                                    sortedDates={sortedDates}
                                    kitchen={kitchen}
                                    moneyFactory={moneyFactory}
                                    totalPrice={totalPrice}
                                    vatAmount={vatAmount}
                                    onCorrectOrder={onCorrectOrder}
                                />
                            </Box>
                        </Box>
                    </Box>
                </SafeArea>
            </Dialog>
        </FormikProvider>
    )
}
